import { Card, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  formatToINR,
  getLast6MonthCategoryWiseAnalysis,
} from '../../../../Utils/utils';
import CategoryAnalysis from './CategoryAnalysis';
import styles from './index.module.css';

const Category: React.FC<{
  currentMonthYearProp: string;
  currentDataKey: string;
  viewAll: () => void;
  isViewAll?: boolean;
  setViewAll?: () => void;
}> = ({
  currentMonthYearProp,
  currentDataKey,
  setViewAll,
  isViewAll,
  viewAll,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [initalData, setInitialData] = useState<any>([]);
  const [lastMonthsDetails, setLastMonthDetails] = useState<any>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (currentMonthYearProp) {
      filterDataMonthWise();
    }
  }, [currentMonthYearProp, currentDataKey]);

  const filterDataMonthWise = () => {
    const filteredResult = initalData.filter((element: any) => {
      if (`${element.month}${element.year}` === currentMonthYearProp) {
        return true;
      }
      return false;
    });

    let totalAmountLocal = 0;

    filteredResult.forEach((r: any) => {
      totalAmountLocal =
        totalAmountLocal +
        (currentDataKey === 'totalDebitAmount'
          ? r.totalDebitAmount
          : r.totalCreditAmount);
    });

    setTotalAmount(totalAmountLocal);

    setData(
      filteredResult
        .sort((a: any, b: any) =>
          currentDataKey === 'totalDebitAmount'
            ? b.totalDebitAmount - a.totalDebitAmount
            : b.totalCreditAmount - a.totalCreditAmount
        )
        .slice(0, 4)
    );
  };
  const customToolTip = ({ active, payload, label }: any) => {
    if (active) {
      return (
        <div className={styles.customTooltip}>
          <Typography.Text className={styles.toolTipLabel}>{`${
            payload[0].payload.category
          } : ${formatToINR(payload[0].value.toString())}`}</Typography.Text>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const getIntialData = () => {
    const result = getLast6MonthCategoryWiseAnalysis();

    setInitialData(result.data);

    const filteredResult = result.data.filter((element: any) => {
      if (`${element.month}${element.year}` === currentMonthYearProp) {
        return true;
      }
      return false;
    });

    let totalAmountLocal = 0;

    filteredResult.forEach((r: any) => {
      totalAmountLocal =
        totalAmountLocal +
        (currentDataKey === 'totalDebitAmount'
          ? r.totalDebitAmount
          : r.totalCreditAmount);
    });

    setTotalAmount(totalAmountLocal);

    setData(
      filteredResult
        .sort((a: any, b: any) =>
          currentDataKey === 'totalDebitAmount'
            ? b.totalDebitAmount - a.totalDebitAmount
            : b.totalCreditAmount - a.totalCreditAmount
        )
        .slice(0, 4)
    );

    setLastMonthDetails(result.Last6Months);

    setLoading(false);
  };

  useEffect(() => {
    getIntialData();
  }, []);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <div className={styles.category_div}>
      {isViewAll ? (
        <CategoryAnalysis setViewAll={setViewAll} />
      ) : (
        <Card
          className={styles.category_card}
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography.Text
                style={{
                  fontSize: '16px',
                  fontWeight: '500',
                }}
              >
                Category Analysis
              </Typography.Text>
              <Typography.Text
                style={{
                  fontSize: '12px',
                  color: `var(--primary-color)`,
                  fontWeight: 'var(--font-medium)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  viewAll();
                }}
              >
                View All
              </Typography.Text>
            </div>
          }
          loading={loading}
        >
          <ResponsiveContainer width={'100%'} height={300}>
            <PieChart width={1000} height={300}>
              <Pie
                data={data}
                innerRadius={70}
                outerRadius={90}
                fill='#8884d8'
                dataKey={
                  currentDataKey === 'totalDebitAmount'
                    ? 'totalDebitAmount'
                    : 'totalCreditAmount'
                }
              >
                {data.map((entry: any, index: any) => (
                  <Cell
                    key={`cell-${entry.categoryCode}`}
                    fill={COLORS[index % COLORS.length]}
                    style={{ outline: 'none' }}
                  />
                ))}
                <Label
                  value='Top Categories'
                  position='center'
                  fill='#000000'
                  fontSize={14}
                />
              </Pie>
              <Tooltip content={customToolTip} />
              <Legend
                align='center' // Align the legend in the center horizontally
                verticalAlign='bottom' // Position the legend at the bottom of the chart
                layout='horizontal' // Set the layout to horizontal
                iconType='circle'
                iconSize={6}
                wrapperStyle={{ wordWrap: 'break-word' }}
                content={({ payload }: any) => {
                  const halfIndex = Math.ceil(payload.length / 2);
                  const firstHalf = payload.slice(0, halfIndex);
                  const secondHalf = payload.slice(halfIndex);

                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <ul
                        style={{
                          width: '48%',
                          overflow: 'hidden',
                          padding: '0',
                        }}
                      >
                        {firstHalf.map((entry: any, index: any) => (
                          <li
                            key={`legend-${index}`}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '5px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '60%',
                              }}
                            >
                              <div
                                style={{
                                  minWidth: '5px ',
                                  height: '5px',
                                  backgroundColor: entry.color,
                                  marginRight: '5px',
                                  borderRadius: '50%',
                                }}
                              ></div>
                              <Typography.Text
                                style={{
                                  color: 'gray',
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: '12px',
                                }}
                              >
                                {' '}
                                {entry.payload.category}
                              </Typography.Text>
                            </div>
                            <Typography.Text
                              style={{
                                fontSize: '12px',
                                color: 'gray',
                              }}
                            >
                              {' '}
                              {`${(
                                (entry.payload.value / totalAmount) *
                                100
                              ).toFixed(2)}%`}
                            </Typography.Text>
                          </li>
                        ))}
                      </ul>
                      <ul
                        style={{
                          width: '48%',
                          overflow: 'hidden',
                          padding: '0',
                        }}
                      >
                        {secondHalf.map((entry: any, index: any) => (
                          <li
                            key={`legend-${halfIndex + index}`}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '5px',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '60%',
                              }}
                            >
                              <div
                                style={{
                                  minWidth: '5px ',
                                  height: '5px',
                                  backgroundColor: entry.color,
                                  marginRight: '5px',
                                  borderRadius: '50%',
                                }}
                              ></div>
                              <Typography.Text
                                style={{
                                  color: 'gray',
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {' '}
                                {entry.payload.category}
                              </Typography.Text>
                            </div>
                            <Typography.Text
                              style={{
                                fontSize: '12px',
                                color: 'gray',
                              }}
                            >
                              {' '}
                              {`${(
                                (entry.payload.value / totalAmount) *
                                100
                              ).toFixed(2)}%`}
                            </Typography.Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
};

export default Category;
